import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { getMarketStatsApi } from '@flipster/apis/v2/market';
import { MarketStatsTypeEnum } from '@flipster/apis/v2/market/types';
import { rawInstrumentsKeyBySymbolAtom } from '@flipster/features/src/shared/snapshot/state';
import { LocaleScope } from '@flipster/i18n';
import { normalizeLocale } from '@flipster/i18n/utils';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';
import { findCryptocurrencyByName } from '@web/app/[locale]/(web)/trade/action';

import { computeHasReadMore, resolveTokenCurrency } from './utils';

/**
 * Token info 패널의 데이터 레이어.
 * 심볼의 base currency를 해석해 CMC market stats / Contentful 소개글을 fetch하고,
 * RWA 여부·Read more 노출 같은 파생값을 atom으로 노출한다.
 * UI(index.tsx)는 이 atom들을 consume해 표시/rich-text 렌더/모달 오픈만 담당한다.
 */
export const tokenInfoBunja = bunja(() => {
  const { symbol } = bunja.use(tradeSymbolBunja);
  // useSupportedLocale()와 동일: LocaleScope(=next-intl useLocale이 읽는 컨텍스트)를 normalize.
  const locale = normalizeLocale(bunja.use(LocaleScope));

  const currencyAtom = atom((get) => resolveTokenCurrency(get(rawInstrumentsKeyBySymbolAtom)?.[symbol]));

  const marketStatsQueryAtom = atomWithQuery((get) => {
    const currency = get(currencyAtom);
    return {
      queryKey: ['getMarketStatsApi', currency],
      queryFn: () => getMarketStatsApi(currency!),
      select: (res: Awaited<ReturnType<typeof getMarketStatsApi>>) => res.data,
      enabled: !!currency,
      refetchOnWindowFocus: false,
    };
  });
  const marketStatsAtom = atom((get) => get(marketStatsQueryAtom).data);

  const isRwaAtom = atom((get) => get(marketStatsAtom)?.type === MarketStatsTypeEnum.Rwa);

  const cryptocurrencyInfoQueryAtom = atomWithQuery((get) => {
    const currency = get(currencyAtom);
    return {
      queryKey: ['getCryptocurrencyApi', currency, locale],
      queryFn: () => findCryptocurrencyByName(currency!, locale),
      // RWA는 Contentful 소개글 대상이 아니므로 조회하지 않는다.
      enabled: !!currency && !get(isRwaAtom),
      refetchOnWindowFocus: false,
    };
  });
  const cryptocurrencyInfoAtom = atom((get) => get(cryptocurrencyInfoQueryAtom).data);

  const hasReadMoreAtom = atom((get) => computeHasReadMore(get(cryptocurrencyInfoAtom), get(isRwaAtom)));

  return {
    symbol,
    currencyAtom,
    marketStatsAtom,
    isRwaAtom,
    cryptocurrencyInfoAtom,
    hasReadMoreAtom,
  };
});
tokenInfoBunja.debugLabel = 'tokenInfoBunja';
